import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Inbox,
  Loader2,
  Minus,
  Music,
  Plus,
} from 'lucide-react';
import {
  fetchConcertEvenement,
  fetchReservationConcert,
  reserverConcert,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { nomFamille, prenomUsage } from '../utils/nom';

import { dateFr } from '../utils/dates';
/** Le bon de réservation se télécharge sur l'API, pas via fetch : lien direct. */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/** Date du concert : c'est sur elle que le spectateur se présente. */
const concertLe = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Réservation des places de concert d'un événement.
 *
 * Même parcours que le repas : combien de places, à quel nom, et on paie. Deux
 * catégories — les places au tarif annoncé, et les places gratuites, qui ne
 * coûtent rien mais occupent un siège. Une réservation entièrement gratuite ne
 * passe pas par Stripe : elle est confirmée sur-le-champ.
 */
export default function ReservationConcert({ evenementId }: { evenementId: number }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [referenceGratuite, setReferenceGratuite] = useState<string | null>(null);

  const [form, setForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    commentaire: '',
  });
  // Les deux compteurs partent de zéro : le spectateur dit ce qu'il veut, il
  // ne corrige pas ce qu'on a décidé pour lui.
  const [payantes, setPayantes] = useState(0);
  const [gratuites, setGratuites] = useState(0);

  const { data, isPending, refetch } = useQuery({
    queryKey: ['concert', evenementId],
    queryFn: () => fetchConcertEvenement(evenementId),
  });

  // Retour de Stripe : le webhook a pu confirmer entre-temps, on relit l'état
  // pour que les places restantes soient justes.
  const retour = params.get('concert');
  useEffect(() => {
    if (retour) refetch();
  }, [retour, refetch]);

  // Un membre connecté ne ressaisit pas ce que le comité connaît déjà.
  useEffect(() => {
    if (!user) return;
    setForm((precedent) => ({
      ...precedent,
      nom: precedent.nom || user.nom || user.fullName || '',
      prenom: precedent.prenom || user.prenom || '',
      email: precedent.email || user.email || '',
      telephone: precedent.telephone || user.telephone || '',
    }));
  }, [user]);

  const reserver = useMutation({
    mutationFn: () =>
      reserverConcert(evenementId, {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        commentaire: form.commentaire.trim() || null,
        nbPayantes: payantes,
        nbGratuites: gratuites,
      }),
    onSuccess: (d: any) => {
      if (d.url) {
        // Le paiement se poursuit chez Stripe ; la place est déjà retenue.
        window.location.href = d.url;
        return;
      }

      // Réservation entièrement gratuite : rien à payer, tout est déjà confirmé.
      setReferenceGratuite(d.reference);
      setOuvert(false);
      refetch();
    },
    onError: (e: Error) => setErreur(e.message),
  });

  if (isPending) return null;

  const concert = data?.concert;
  if (!concert?.actif) return null;

  const total = payantes * concert.prixPlace;
  const invalide =
    form.nom.trim() === '' || !form.email.includes('@') || payantes + gratuites === 0;

  return (
    <section className="card lg:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-violet-50 text-violet-700 shrink-0">
          <Music className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h2 className="font-semibold lg:text-lg">Concert sur réservation</h2>
          {concert.date && (
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5 first-letter:uppercase">
              {concertLe.format(new Date(concert.date))}
            </p>
          )}
          {concert.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line mt-0.5">
              {concert.description}
            </p>
          )}
        </div>
      </div>

      {(retour === 'succes' || referenceGratuite) && (
        <Confirmation reference={referenceGratuite ?? params.get('reference')} />
      )}
      {retour === 'annule' && (
        <p className="text-sm rounded-xl px-3 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
          Paiement abandonné. Votre place n'a pas été retenue.
        </p>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {concert.prixPlace > 0 && (
          <span>
            Place <strong>{concert.prixPlace} €</strong>
          </span>
        )}
        {concert.quotaGratuit > 0 && (
          <span>
            Place gratuite <strong>0 €</strong>
          </span>
        )}
        {concert.dateLimite && (
          <span className="text-gray-500 dark:text-gray-400">
            Réservations jusqu'au {dateFr(concert.dateLimite)}
          </span>
        )}
      </div>

      {/* Places restantes — l'information qui décide le visiteur. */}
      {!concert.complet && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {concert.restantTotal <= 10 ? (
            <span className="text-amber-700 font-medium">
              Plus que {concert.restantTotal} place{concert.restantTotal > 1 ? 's' : ''} disponible
              {concert.restantTotal > 1 ? 's' : ''}.
            </span>
          ) : (
            <>
              {concert.restantPayant} place{concert.restantPayant > 1 ? 's' : ''} au tarif normal
              {concert.restantGratuit > 0 && <> et {concert.restantGratuit} gratuite(s)</>} encore
              disponibles.
            </>
          )}
        </p>
      )}

      {concert.complet ? (
        <button
          type="button"
          disabled
          className="w-full py-3 rounded-xl bg-gray-200 text-gray-500 font-semibold cursor-not-allowed dark:text-gray-400"
        >
          Complet
        </button>
      ) : !concert.ouvert ? (
        <button
          type="button"
          disabled
          className="w-full py-3 rounded-xl bg-gray-200 text-gray-500 font-semibold cursor-not-allowed dark:text-gray-400"
        >
          Réservations closes
        </button>
      ) : !ouvert ? (
        <button
          type="button"
          onClick={() => setOuvert(true)}
          className="btn-primary w-full justify-center py-3"
        >
          Réserver mes places
        </button>
      ) : (
        <div className="space-y-3">
          <Compteur
            libelle="Places"
            prix={concert.prixPlace}
            valeur={payantes}
            max={concert.restantPayant}
            onChange={setPayantes}
          />
          <Compteur
            libelle="Places gratuites"
            prix={0}
            valeur={gratuites}
            max={concert.restantGratuit}
            onChange={setGratuites}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className="input"
              placeholder="Nom *"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: nomFamille(e.target.value) })}
            />
            <input
              className="input"
              placeholder="Prénom"
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: prenomUsage(e.target.value) })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-mail *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="input"
              type="tel"
              placeholder="Téléphone"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            />
          </div>

          <textarea
            className="input min-h-[4rem]"
            placeholder="Accessibilité, place à côté d'un proche…"
            value={form.commentaire}
            onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
          />

          <p className="text-xs text-gray-500 dark:text-gray-400">
            La confirmation part à cette adresse : elle porte votre code d'entrée.
          </p>

          {erreur && (
            <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {erreur}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <span className="text-sm text-gray-600 dark:text-gray-400">Total</span>
            <span className="text-xl font-bold text-primary-700">{total} €</span>
          </div>

          <button
            type="button"
            onClick={() => {
              setErreur(null);
              setParams(new URLSearchParams());
              reserver.mutate();
            }}
            disabled={invalide || reserver.isPending}
            className="btn-primary w-full justify-center py-3 disabled:opacity-50"
          >
            {reserver.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {total > 0 ? `Payer ${total} € et réserver` : 'Réserver mes places gratuites'}
          </button>

          {total > 0 && (
            <p className="text-xs text-gray-400 text-center">
              Paiement sécurisé par Stripe — carte bancaire, Apple&nbsp;Pay et Google&nbsp;Pay.
            </p>
          )}

          <button
            type="button"
            onClick={() => setOuvert(false)}
            className="text-sm text-gray-500 w-full text-center dark:text-gray-400"
          >
            Annuler
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Écran de retour de réservation.
 *
 * C'est le seul moment où le spectateur a la référence sous les yeux.
 */
function Confirmation({ reference }: { reference: string | null }) {
  // Le code d'entrée n'est pas dans l'adresse de retour : on le relit sur la
  // référence. C'est lui que le spectateur présentera à la porte.
  const { data } = useQuery({
    queryKey: ['reservation-concert', reference],
    queryFn: () => fetchReservationConcert(reference as string),
    enabled: reference !== null,
    retry: false,
  });

  const code = data?.reservation?.codeRetrait ?? null;

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-800 p-4 lg:p-5 space-y-3">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-6 h-6 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-lg lg:text-xl font-semibold text-violet-900 dark:text-violet-100">
            Vos places sont réservées
          </p>
          <p className="text-sm text-violet-800/80 dark:text-violet-200/80 mt-0.5">
            Merci, et à bientôt dans la salle.
          </p>
        </div>
      </div>

      {(code || reference) && (
        <div className="rounded-xl bg-white dark:bg-gray-900 border border-violet-200 dark:border-violet-800 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {code ? "Votre code d'entrée — notez-le" : 'Votre référence — notez-la'}
          </p>
          <p className="font-mono text-xl lg:text-2xl font-bold text-violet-700 dark:text-violet-400 tracking-wider">
            {code ?? reference}
          </p>
          {code && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              À présenter à l'entrée, avec ou sans QR code. Référence de la réservation :{' '}
              <span className="font-mono">{reference}</span>
            </p>
          )}
        </div>
      )}

      <p className="text-base text-violet-900/90 dark:text-violet-100/90 leading-relaxed">
        Un e-mail de confirmation vient de partir, avec votre code d'entrée.
        <span className="block mt-1 flex items-start gap-1.5">
          <Inbox className="w-4 h-4 mt-1 shrink-0 opacity-70" />
          <span>
            S'il n'arrive pas, pensez à regarder dans vos <strong>indésirables</strong> ou
            vos <strong>spams</strong>.
          </span>
        </span>
      </p>

      {reference && (
        <a
          href={`${API_BASE}/concert/public/reservation/${encodeURIComponent(reference)}/bon.pdf`}
          className="btn-primary w-full justify-center py-3 inline-flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Télécharger mon bon de réservation
        </a>
      )}
    </div>
  );
}

/**
 * Sélecteur de quantité, borné par les places restantes.
 */
function Compteur({
  libelle,
  prix,
  valeur,
  max,
  onChange,
}: {
  libelle: string;
  prix: number;
  valeur: number;
  max: number;
  onChange: (v: number) => void;
}) {
  if (max === 0 && valeur === 0) return null;

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 dark:border-gray-800">
      <div className="min-w-0">
        <p className="text-sm font-medium">{libelle}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {prix > 0 ? `${prix} €` : 'Gratuit'} — {max} restante{max > 1 ? 's' : ''}
        </p>
      </div>

      <div className="inline-flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, valeur - 1))}
          disabled={valeur === 0}
          aria-label={`Retirer une ${libelle.toLowerCase()}`}
          className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-8 text-center font-semibold tabular-nums">{valeur}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, valeur + 1))}
          disabled={valeur >= max}
          aria-label={`Ajouter une ${libelle.toLowerCase()}`}
          className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
